import type { Messages } from "@/i18n/locales/en";
import { legalMessagesRo } from "@/i18n/locales/legalRoData";
import { pagesMessagesRo } from "@/i18n/locales/pagesRoData";

export const roMessages = {
  meta: {
    description:
      "WasOK — scanați produse, salvați evaluări și note, amintiți-vă ce a meritat cumpărat.",
    title: "WasOK — Amintiți-vă ce a meritat cumpărat",
  },
  header: {
    navFeatures: "Funcții",
    navHowItWorks: "Cum funcționează",
    navPrivacy: "Confidențialitate",
    navUseCases: "Cazuri de utilizare",
    download: "Descarcă",
    openMenu: "Deschide meniul",
    closeMenu: "Închide meniul",
    languageAria: "Limbă",
  },
  hero: {
    badge: "📱 Disponibil acum pe Android",
    title: "Amintiți-vă ce a meritat cumpărat.",
    subtitle:
      "Scanați produse, salvați evaluări și note — și amintiți-vă instant dacă un produs a fost cu adevărat bun.",
    googlePlay: "Descarcă pe Google Play",
    watchDemo: "Vezi demonstrația",
    privacyLine: "Toate datele rămân pe dispozitivul dvs.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Scanați pentru a vă aminti",
    snackCard: "Delicios!",
    coffeeCard: "Prea amar",
    scrollHint: "Derulați pentru a explora",
  },
  problems: {
    heading: "Vi se pare familiar?",
    subheading:
      "Am trecut cu toții prin asta — uitându-ne la produse, încercând să ne amintim dacă ne-au plăcut data trecută.",
    notedAgo: "✓ Ați notat asta acum 3 luni",
    items: [
      {
        question: "Ce ciocolată mi-a plăcut cu adevărat?",
        note: "Dulceață perfectă",
      },
      {
        question: "De ce am renunțat să cumpăr acest șampon?",
        note: "Mi-a uscat părul",
      },
      {
        question: "Această cafea a fost bună sau dezamăgitoare?",
        note: "Prea amar",
      },
    ],
  },
  howItWorks: {
    heading: "Cum funcționează",
    subheading:
      "Patru pași simpli ca să nu uitați niciodată opiniile despre produse.",
    stepPrefix: "Pasul",
    steps: [
      {
        title: "Scanați orice cod de bare de produs",
        description:
          "Scanare rapidă și ușoară cu camera. Funcționează cu orice cod de bare.",
      },
      {
        title: "Salvați evaluări, comentarii și fotografii",
        description:
          "Evaluați produse, adăugați note, atașați fotografii și înregistrați prețuri și locații.",
      },
      {
        title: "Amintiți-vă instant experiența",
        description:
          "Scanați același produs mai târziu și vedeți evaluarea și notele anterioare.",
      },
      {
        title: "Creați liste de cumpărături inteligente",
        description:
          "Creați liste de cumpărături din produsele în care aveți deja încredere și pe care le iubiți.",
      },
    ],
  },
  privacy: {
    heading: "Istoricul cumpărăturilor rămâne al dvs.",
    body: "WasOK vă ajută să vă amintiți produsele pe care le-ați încercat, păstrând evaluările, notele, fotografiile și listele de cumpărături stocate local pe dispozitivul dvs.",
    trustBanner:
      "💚 Construit în jurul istoricului cumpărăturilor — nu în jurul colectării datelor dvs.",
    bullets: [
      "Nu este necesar un cont",
      "Nu este necesară sincronizarea în cloud",
      "Stocare local-first",
      "Backup-uri controlate de utilizator",
      "Căutare opțională a codului de bare pentru informații despre produs.",
      "Susținut de reclame cu Google AdMob",
    ],
  },
  features: {
    heading: "Tot ce aveți nevoie",
    subheading:
      "Funcții puternice concepute pentru a face urmărirea produselor simplă și privată.",
    items: [
      {
        title: "Scanner de coduri de bare",
        description: "Scanare rapidă și precisă a codurilor de bare pentru orice produs.",
      },
      {
        title: "Evaluări de produse",
        description: "Evaluați produsele cu 1–5 stele pentru a vă aminti calitatea.",
      },
      {
        title: "Fotografii de produse",
        description: "Atașați fotografii pentru a vă aminti ambalajul sau detaliile.",
      },
      {
        title: "Categorii",
        description: "Organizați produsele în categorii personalizate.",
      },
      {
        title: "Urmărirea magazinelor",
        description: "Amintiți-vă de unde ați cumpărat fiecare produs.",
      },
      {
        title: "Înregistrarea prețurilor",
        description: "Urmăriți prețurile pentru a găsi cele mai bune oferte.",
      },
      {
        title: "Liste de cumpărături",
        description: "Creați liste din produsele preferate.",
      },
      {
        title: "Acces offline",
        description: "Funcționează perfect fără conexiune la internet.",
      },
      {
        title: "Stocare locală",
        description: "Toate datele dvs. sunt stocate în siguranță pe dispozitiv.",
      },
    ],
  },
  useCases: {
    heading: "Perfect pentru fiecare raft",
    subheading:
      "Fie că cumpărați alimente, produse de frumusețe sau articole de uz casnic — WasOK vă ajută să vă amintiți.",
    cards: [
      {
        emoji: "🍫",
        title: "Gustări și dulciuri",
        description: "Amintiți-vă care bomboane au fost cu adevărat bune.",
      },
      {
        emoji: "☕",
        title: "Cafea și băuturi",
        description: "Urmăriți cafelele care merită cumpărate din nou.",
      },
      {
        emoji: "💄",
        title: "Cosmetice și îngrijirea pielii",
        description: "Păstrați note despre reacțiile pielii.",
      },
      {
        emoji: "🧹",
        title: "Articole de uz casnic",
        description: "Evitați să cumpărați din nou produse care au eșuat.",
      },
    ],
  },
  screenshots: {
    heading: "Frumos și intuitiv",
    subheading:
      "O interfață gândită cu grijă care face urmărirea produselor o plăcere.",
    items: [
      { title: "Scanner" },
      { title: "Detaliu produs" },
      { title: "Introducere evaluare" },
      { title: "Căutare și istoric" },
      { title: "Listă de cumpărături" },
    ],
  },
  cta: {
    heading: "Memorie de cumpărături pentru viața reală.",
    body: "Nu mai uitați care produse au meritat cumpărate. Începeți să cumpărați mai inteligent astăzi.",
    googlePlay: "Descarcă pe Google Play",
    scanQr: "Scanați pentru a descărca",
    tagline: "Privat · Offline-first · Simplu",
  },
  footer: {
    tagline: "Amintiți-vă ce a meritat cumpărat.",
    linksHeading: "Linkuri",
    privacyPolicy: "Politica de confidențialitate",
    terms: "Termeni de utilizare",
    contact: "Contact",
    linkFaq: "Întrebări frecvente",
    linkLicenses: "Licențe open source",
    linkAdvertising: "Publicitate și monetizare",
    downloadHeading: "Descarcă",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Toate drepturile rezervate. Făcut cu 💚 pentru cumpărături mai inteligente.",
  },
  legal: legalMessagesRo,
  faq: pagesMessagesRo.faq,
  support: pagesMessagesRo.support,
  advertising: pagesMessagesRo.advertising,
} satisfies Messages;
